import { useEffect, useState } from "react";
import axios from "axios";
import { Tr, Td, Badge, Icon, Tooltip, Flex, Text } from "@chakra-ui/react";
import { ChevronUpIcon, ChevronDownIcon, MinusIcon, TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";

const Stock = ({ stock }) => {
  const [price, setPrice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const sheetId = '1BlwDjEl8TyX0KyRXu_zKiirw69JI5-HcE_b7Ezc5d-g';
    const apiKey = 'AIzaSyA68-jfMFAdxQOZXW_yEH7H1yj62drMwl0';
    const range = 'Sheet1!D:D';
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}?key=${apiKey}`;

    axios
      .get(url)
      .then((response) => {
        const priceValue = response.data.values[stock.id];
        if (priceValue) {
          const cleanedPrice = priceValue[0].replace('$', '');
          setPrice(parseFloat(cleanedPrice));
        }
      })
      .catch((error) => {
        console.error('Eroare la încărcarea datelor:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [stock.id]);

  const totalPrice = price * stock.nr_shares;
  const avgPriceTotal = stock.average_price_share * stock.nr_shares;
  const difference = totalPrice - avgPriceTotal + +stock.paid_div;
  const percentageChange = (difference / avgPriceTotal) * 100;
  const dividendTotal = stock.div_share * stock.div_per_year * stock.nr_shares;
  const recover = stock.paid_div / (avgPriceTotal + +stock.commission) * 100;

  const profit = totalPrice - avgPriceTotal - stock.commission;
  const isProfit = profit >= 0;

  return (
    <Tr
      _hover={{ 
        bg: "gray.700", 
        transform: "scale(1.002)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
      }}
      transition="all 0.2s ease"
      borderBottom="1px solid rgba(255, 255, 255, 0.1)"
    >
      <Td textAlign="center" fontWeight="semibold" color="teal.300">
        {stock.company}
      </Td>
      
      <Td textAlign="center">
        <Badge colorScheme="purple" fontSize="xs" px={2} py={1} borderRadius="md">
          {stock.ticker}
        </Badge>
      </Td>
      
      <Td textAlign="center" fontSize="sm">
        {stock.nr_shares}
      </Td>
      
      <Td textAlign="center" fontWeight="medium" fontSize="sm">
        {isLoading ? (
          <Text color="gray.500">...</Text>
        ) : (
          `$${price ? price.toFixed(2) : "0.00"}`
        )}
      </Td>
      
      <Td textAlign="center" fontWeight="bold" color="blue.300" fontSize="sm">
        ${totalPrice ? totalPrice.toFixed(2) : "0.00"}
      </Td>
      
      <Td textAlign="center" fontSize="sm">
        ${stock.average_price_share}
      </Td>
      
      <Td textAlign="center" color="gray.400" fontSize="sm">
        ${avgPriceTotal ? avgPriceTotal.toFixed(2) : "0.00"}
      </Td>
      
      <Td textAlign="center" fontWeight="bold" fontSize="sm">
        <Tooltip
          label={`Total gain/loss including dividends: $${difference.toFixed(2)}`}
          hasArrow
          placement="top"
        >
          <Flex align="center" justify="center" gap={1}>
            <Icon
              as={difference >= 0 ? TriangleUpIcon : TriangleDownIcon}
              color={difference >= 0 ? "green.400" : "red.400"}
              boxSize={3}
            />
            <Text color={difference >= 0 ? "green.400" : "red.400"}>
              ${Math.abs(difference).toFixed(2)}
            </Text>
          </Flex>
        </Tooltip>
      </Td>
      
      <Td textAlign="center">
        <Tooltip
          label={
            percentageChange > 0
              ? `You gained ${percentageChange.toFixed(2)}% on this investment.`
              : percentageChange < 0
              ? `You lost ${Math.abs(percentageChange).toFixed(2)}% on this investment.`
              : `No gain or loss on this investment.`
          }
          hasArrow
          placement="top"
        >
          <Badge
            bgGradient={
              percentageChange > 0
                ? "linear(to-r, green.400, green.600)"
                : percentageChange < 0
                ? "linear(to-r, red.400, red.600)"
                : "linear(to-r, gray.300, gray.400)"
            }
            color="white"
            fontSize="0.7em"
            px={3}
            py={1}
            borderRadius="xl"
            boxShadow="md"
            display="inline-flex"
            alignItems="center"
            gap={1}
            _hover={{ transform: "scale(1.05)" }}
            transition="transform 0.2s"
          >
            <Icon
              as={
                percentageChange > 0
                  ? ChevronUpIcon
                  : percentageChange < 0
                  ? ChevronDownIcon
                  : MinusIcon
              }
              boxSize={3}
            />
            {percentageChange.toFixed(2)}%
          </Badge>
        </Tooltip>
      </Td>
      
      <Td textAlign="center" fontSize="sm">
        ${stock.div_share}
      </Td>
      
      <Td textAlign="center" fontWeight="semibold" color="green.300" fontSize="sm">
        <Tooltip
          label="Expected annual dividend income from this stock"
          hasArrow
          placement="top"
        >
          ${dividendTotal ? dividendTotal.toFixed(2) : "0.00"}
        </Tooltip>
      </Td>
      
      <Td textAlign="center" color="yellow.300" fontWeight="medium" fontSize="sm">
        <Tooltip
          label="Total dividends received so far"
          hasArrow
          placement="top"
        >
          ${stock.paid_div}
        </Tooltip>
      </Td>
      
      <Td textAlign="center">
        <Badge 
          colorScheme={recover >= 100 ? "green" : recover >= 50 ? "yellow" : "red"} 
          fontSize="xs" 
          px={2} 
          py={1}
          borderRadius="md"
        >
          <Tooltip
            label={`You've recovered ${recover.toFixed(2)}% of your initial investment through dividends`}
            hasArrow
            placement="top"
          >
            {recover ? recover.toFixed(2) : "0.00"}%
          </Tooltip>
        </Badge>
      </Td>
      
      <Td textAlign="center" color="gray.500" fontSize="sm">
        ${stock.commission}
      </Td>
    </Tr>
  );
};

export default Stock;